import { escapeHtml } from './sedrEscape';

/** Matches `[Display Name](sedr-file:<fileId>)` tokens in comment bodies. */
const SEDR_COMMENT_FILE_TOKEN_RE = /\[([^\]]+)\]\(sedr-file:([A-Za-z0-9_-]+)\)/g;

/** Sanitize a file name so it is safe inside a `[label](sedr-file:…)` token. */
export function sanitizeSedrCommentFileLabel(name: string): string {
  return name.replace(/[\[\]]/g, '').trim() || 'file';
}

/** Build an inline file-link token for a comment body. */
export function formatSedrCommentFileToken(fileId: string, displayName: string): string {
  return `[${sanitizeSedrCommentFileLabel(displayName)}](sedr-file:${fileId})`;
}

/**
 * Render a comment body as HTML: escape plaintext and turn sedr-file tokens
 * into file-link markup via `renderFileLink`.
 */
export function renderSedrCommentBodyHtml(
  body: string,
  renderFileLink: (fileId: string | null, fallbackName: string | null) => string,
): string {
  SEDR_COMMENT_FILE_TOKEN_RE.lastIndex = 0;
  let result = '';
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = SEDR_COMMENT_FILE_TOKEN_RE.exec(body)) !== null) {
    result += escapeHtml(body.slice(lastIndex, match.index));
    result += renderFileLink(match[2], match[1]);
    lastIndex = match.index + match[0].length;
  }
  result += escapeHtml(body.slice(lastIndex));
  return result;
}

/**
 * Insert a file-link token at the textarea cursor, adding spaces so it does
 * not glue to adjacent words. Returns false if the insert would exceed maxLength.
 */
export function insertSedrCommentFileToken(
  textarea: HTMLTextAreaElement,
  token: string,
): boolean {
  const start = textarea.selectionStart ?? textarea.value.length;
  const end = textarea.selectionEnd ?? start;
  const before = textarea.value.slice(0, start);
  const after = textarea.value.slice(end);
  const needsSpaceBefore = before.length > 0 && !/\s$/.test(before);
  const needsSpaceAfter = after.length > 0 && !/^\s/.test(after);
  const insertion =
    (needsSpaceBefore ? ' ' : '') + token + (needsSpaceAfter ? ' ' : '');
  const next = before + insertion + after;
  const max = textarea.maxLength > 0 ? textarea.maxLength : Number.POSITIVE_INFINITY;
  if (next.length > max) return false;
  textarea.value = next;
  const caret = before.length + insertion.length;
  textarea.setSelectionRange(caret, caret);
  textarea.focus();
  textarea.dispatchEvent(new Event('input', { bubbles: true }));
  return true;
}
